import React, { useState, useEffect, useRef, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import CourseService from "../../../Api/CourseService";
import StudentProgress from "../../../Api/StudentProgress";
import Player from '@vimeo/player';
import '../../../styles/common.css';
import './LessionControls.css';

const Lession = () => {
    const [accordionStates, setAccordionStates] = useState({});
    const [courseData, setCourseData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [currentVideo, setCurrentVideo] = useState(null);
    const [currentCategoryIndex, setCurrentCategoryIndex] = useState(0);
    const [currentVideoIndex, setCurrentVideoIndex] = useState(0);
    const [progressData, setProgressData] = useState(null);
    const [videoProgress, setVideoProgress] = useState({});
    const playerRef = useRef(null);
    const progressIntervalRef = useRef(null);
    const location = useLocation();
    const navigate = useNavigate();
    const courseService = new CourseService();
    const studentProgress = new StudentProgress();
    const watermarkRef = useRef(null);
    const animationIntervalRef = useRef(null);
    const [watermarkPos, setWatermarkPos] = useState({ x: 20, y: 20 });
    const posRef = useRef({ x: 20, y: 20 });
    const dirRef = useRef({ dx: 2, dy: 2 });

    // New states for custom controls
    const [isPlaying, setIsPlaying] = useState(false);
    const [currentTime, setCurrentTime] = useState(0);
    const [duration, setDuration] = useState(0);
    const [volume, setVolume] = useState(1);
    const [isMuted, setIsMuted] = useState(false);

    // States for player dimensions (for responsive watermark)
    const [playerWidth, setPlayerWidth] = useState(0);
    const [playerHeight, setPlayerHeight] = useState(0);
    const [watermarkRelativeWidth, setWatermarkRelativeWidth] = useState(0); // Watermark width as a fraction of player width
    const [watermarkRelativeHeight, setWatermarkRelativeHeight] = useState(0); // Watermark height as a fraction of player height

    useEffect(() => {
        const fetchCourseData = async () => {
            try {
                // Get courseId from navigation state
                const courseId = location.state?.courseId;
                if (!courseId) {
                    setError("No course selected");
                    setLoading(false);
                    return;
                }

                const response = await courseService.GetforsaleById(courseId);
                
                if (response.data.status === "success") {
                    setCourseData(response.data.data);
                    // Initialize accordion states for each category
                    const initialStates = {};
                    response.data.data.categories.forEach((category, index) => {
                        initialStates[`section${index + 1}`] = false;
                    });
                    setAccordionStates(initialStates);

                    // Fetch progress data
                    try {
                        const progressResponse = await studentProgress.GetStudentProgressByCourse(courseId);
                        if (progressResponse.data.status === "success") {
                            setProgressData(progressResponse.data.data);
                            // Initialize video progress map
                            const progressMap = {};
                            progressResponse.data.data.video_progress.forEach(progress => {
                                progressMap[progress.video_id] = progress;
                            });
                            setVideoProgress(progressMap);
                        }
                    } catch (progressError) {
                        console.error("Error fetching progress:", progressError);
                        // Initialize empty progress map if fetch fails
                        setVideoProgress({});
                    }

                    // Play first video by default
                    if (response.data.data.categories.length > 0 && 
                        response.data.data.categories[0].videos.length > 0) {
                        const firstVideo = response.data.data.categories[0].videos[0];
                        handleVideoClick(firstVideo);
                    }
                }
                setLoading(false);
            } catch (err) {
                setError("Failed to fetch course data");
                setLoading(false);
                console.error("Error fetching course:", err);
            }
        };

        fetchCourseData();
    }, [location.state]);

    useEffect(() => {
        // Initialize Vimeo Player when component mounts
        if (currentVideo?.vimeo_url) {
            const videoId = currentVideo.vimeo_url.split('/').pop();
            const playerElement = document.getElementById('vimeo-player');
            
            if (playerElement) {
                // Append Vimeo embed parameters to the URL to hide UI elements and native controls
                const embedUrl = `https://player.vimeo.com/video/${videoId}?autoplay=1&loop=0&title=0&byline=0&portrait=0&controls=0&dnt=1`;

                playerRef.current = new Player(playerElement, {
                    url: embedUrl,
                    width: '100%',
                    height: '100%',
                    autopause: false,
                    autoplay: true,
                    controls: false, // Explicitly disable native controls
                    muted: isMuted // Set initial mute state
                });
                
                // Add event listeners for custom controls
                playerRef.current.on('play', () => {
                    setIsPlaying(true);
                    startProgressTracking();
                });

                playerRef.current.on('pause', () => {
                    setIsPlaying(false);
                    stopProgressTracking();
                });

                playerRef.current.on('timeupdate', (data) => {
                    setCurrentTime(data.seconds);
                    setDuration(data.duration);
                });

                playerRef.current.on('volumechange', (data) => {
                    setVolume(data.volume);
                    setIsMuted(data.muted);
                });

                playerRef.current.on('ended', async () => {
                    console.log('Video ended');
                    setIsPlaying(false);
                    stopProgressTracking();
                    if (playerRef.current) {
                        await playerRef.current.unload(); // Unload the current video
                    }
                    if (currentVideo) {
                        try {
                            await studentProgress.UpdateCompletedVideo(currentVideo.id);
                            // Update local progress state
                            setVideoProgress(prev => ({
                                ...prev,
                                [currentVideo.id]: {
                                    ...prev[currentVideo.id],
                                    is_completed: true,
                                    completion_percentage: 100
                                }
                            }));
                        } catch (error) {
                            console.error('Error marking video as completed:', error);
                        }
                    }
                    handleNextVideo(); // Play the next video
                });

                playerRef.current.on('error', (error) => {
                    console.error('Player error:', error);
                    stopProgressTracking();
                });
            }
        }

        // Cleanup
        return () => {
            stopProgressTracking();
            if (playerRef.current) {
                playerRef.current.destroy();
                playerRef.current = null; // Clear ref after destroying
            }
        };
    }, [currentVideo, isMuted]); // Depend on isMuted to re-initialize player with correct mute state

    const startProgressTracking = () => {
        if (progressIntervalRef.current) return;

        progressIntervalRef.current = setInterval(async () => {
            if (playerRef.current && currentVideo) {
                try {
                    const currentTime = await playerRef.current.getCurrentTime();
                    const duration = await playerRef.current.getDuration();
                    
                    // Update progress every 30 seconds or if video is almost ended
                    if (Math.floor(currentTime) % 30 === 0 || (duration - currentTime) < 1) {
                        await studentProgress.UpdateVideoProgress(currentVideo.id, {
                            current_time: Math.floor(currentTime),
                            video_duration: Math.floor(duration)
                        });

                        // Update local progress state
                        setVideoProgress(prev => ({
                            ...prev,
                            [currentVideo.id]: {
                                ...prev[currentVideo.id],
                                watched_duration: Math.floor(currentTime),
                                total_duration: Math.floor(duration),
                                completion_percentage: Math.floor((currentTime / duration) * 100)
                            }
                        }));
                    }
                } catch (error) {
                    console.error('Error updating video progress:', error);
                }
            }
        }, 1000); // Check every second
    };

    const stopProgressTracking = () => {
        if (progressIntervalRef.current) {
            clearInterval(progressIntervalRef.current);
            progressIntervalRef.current = null;
        }
    };

    const findVideoIndices = (videoId) => {
        if (!courseData?.categories) return { categoryIndex: 0, videoIndex: 0 };
        
        for (let categoryIndex = 0; categoryIndex < courseData.categories.length; categoryIndex++) {
            const category = courseData.categories[categoryIndex];
            const videoIndex = category.videos.findIndex(v => v.id === videoId);
            if (videoIndex !== -1) {
                return { categoryIndex, videoIndex };
            }
        }
        return { categoryIndex: 0, videoIndex: 0 };
    };

    const handleVideoClick = async (video) => {
        try {
            setCurrentVideo(video);
            const { categoryIndex, videoIndex } = findVideoIndices(video.id);
            setCurrentCategoryIndex(categoryIndex);
            setCurrentVideoIndex(videoIndex);
            
            // Extract video ID from vimeo_url
            const videoId = video.vimeo_url.split('/').pop();
            if (playerRef.current) {
                await playerRef.current.loadVideo(videoId);
                await playerRef.current.play();
            }
        } catch (error) {
            console.error('Error playing video:', error);
        }
    };

    const handleNextVideo = async () => {
        if (!courseData?.categories) return;

        const currentCategory = courseData.categories[currentCategoryIndex];
        let nextCategoryIndex = currentCategoryIndex;
        let nextVideoIndex = currentVideoIndex + 1;

        // If we're at the end of the current category
        if (nextVideoIndex >= currentCategory.videos.length) {
            // Move to the next category if available
            if (currentCategoryIndex + 1 < courseData.categories.length) {
                nextCategoryIndex = currentCategoryIndex + 1;
                nextVideoIndex = 0;
            } else {
                // We're at the end of all videos
                return;
            }
        }

        const nextVideo = courseData.categories[nextCategoryIndex].videos[nextVideoIndex];
        await handleVideoClick(nextVideo);
    };

    const handlePreviousVideo = async () => {
        if (!courseData?.categories) return;

        const currentCategory = courseData.categories[currentCategoryIndex];
        let prevCategoryIndex = currentCategoryIndex;
        let prevVideoIndex = currentVideoIndex - 1;

        // If we're at the start of the current category
        if (prevVideoIndex < 0) {
            // Move to the previous category if available
            if (currentCategoryIndex > 0) {
                prevCategoryIndex = currentCategoryIndex - 1;
                prevVideoIndex = courseData.categories[prevCategoryIndex].videos.length - 1;
            } else {
                // We're at the start of all videos
                return;
            }
        }

        const prevVideo = courseData.categories[prevCategoryIndex].videos[prevVideoIndex];
        await handleVideoClick(prevVideo);
    };

    const toggleAccordion = (section) => {
        setAccordionStates(prev => ({
            ...prev,
            [section]: !prev[section]
        }));
    };

    // Custom control functions
    const togglePlayPause = () => {
        if (!playerRef.current) return;
        if (isPlaying) {
            playerRef.current.pause();
        } else {
            playerRef.current.play();
        }
    };

    const handleSeek = (e) => {
        if (!playerRef.current) return;
        const progressBar = e.currentTarget;
        const clickPosition = (e.clientX - progressBar.getBoundingClientRect().left) / progressBar.offsetWidth;
        playerRef.current.setCurrentTime(duration * clickPosition);
    };

    const toggleMute = () => {
        if (!playerRef.current) return;
        const newMutedState = !isMuted;
        playerRef.current.setMuted(newMutedState);
        setIsMuted(newMutedState);
    };

    const handleVolumeChange = (e) => {
        if (!playerRef.current) return;
        const newVolume = parseFloat(e.target.value);
        playerRef.current.setVolume(newVolume);
        setVolume(newVolume);
        if (newVolume > 0 && isMuted) {
            setIsMuted(false);
        }
    };

    const formatTime = (seconds) => {
        const minutes = Math.floor(seconds / 60);
        const remainingSeconds = Math.floor(seconds % 60);
        return `${minutes}:${remainingSeconds < 10 ? '0' : ''}${remainingSeconds}`;
    };

    // The new animation logic using posRef and dirRef is already present above

    // Effect to observe player dimensions and update state
    useEffect(() => {
        const playerElement = document.getElementById('vimeo-player');
        if (!playerElement) return;

        const resizeObserver = new ResizeObserver(entries => {
            for (let entry of entries) {
                if (entry.contentBoxSize) {
                    setPlayerWidth(entry.contentRect.width);
                    setPlayerHeight(entry.contentRect.height);
                }
            }
        });

        resizeObserver.observe(playerElement);

        // Initial measurement
        const initialPlayerRect = playerElement.getBoundingClientRect();
        if (initialPlayerRect.width > 0 && initialPlayerRect.height > 0) {
            setPlayerWidth(initialPlayerRect.width);
            setPlayerHeight(initialPlayerRect.height);
        }

        return () => {
            resizeObserver.disconnect();
        };
    }, []); // Runs once on mount

    // Effect to calculate watermark's relative size and set initial position
    useEffect(() => {
        const watermarkElement = watermarkRef.current;
        const playerElement = document.getElementById('vimeo-player');

        if (watermarkElement && playerElement && playerWidth > 0 && playerHeight > 0) {
            const wmRect = watermarkElement.getBoundingClientRect();
            // Ensure valid dimensions before setting relative sizes
            if (wmRect.width > 0 && wmRect.height > 0) {
                setWatermarkRelativeWidth(wmRect.width / playerWidth);
                setWatermarkRelativeHeight(wmRect.height / playerHeight);

                // Set initial random position AFTER dimensions are stable
                const initialX = Math.random() * (1 - (wmRect.width / playerWidth)); 
                const initialY = Math.random() * (1 - (wmRect.height / playerHeight)); 
                setWatermarkPos({ x: initialX, y: initialY });
                dirRef.current = { dx: (Math.random() > 0.5 ? 1 : -1), dy: (Math.random() > 0.5 ? 1 : -1) };
            }
        }
    }, [playerWidth, playerHeight, watermarkRef.current]); // Depend on player dimensions and watermark ref

    // Effect to manage watermark animation
    useEffect(() => {
        let animationFrameId;

        const animate = () => {
            const playerElement = document.getElementById('vimeo-player');
            if (!playerElement || !watermarkRef.current) {
                animationFrameId = requestAnimationFrame(animate);
                return;
            }
            const watermarkRect = watermarkRef.current.getBoundingClientRect();
            const maxX = playerElement.offsetWidth - watermarkRect.width;
            const maxY = playerElement.offsetHeight - watermarkRect.height;

            let { x, y } = posRef.current;
            let { dx, dy } = dirRef.current;

            if (x + dx > maxX || x + dx < 0) dx = -dx;
            if (y + dy > maxY || y + dy < 0) dy = -dy;

            x = Math.max(0, Math.min(x + dx, maxX));
            y = Math.max(0, Math.min(y + dy, maxY));

            posRef.current = { x, y };
            dirRef.current = { dx, dy };

            setWatermarkPos({ x, y });

            animationFrameId = requestAnimationFrame(animate);
        };

        animationFrameId = requestAnimationFrame(animate);
        return () => cancelAnimationFrame(animationFrameId);
    }, [currentVideo]);

    if (loading) {
        return <div className="rbt-lesson-area bg-color-white">Loading...</div>;
    }

    if (error) {
        return <div className="rbt-lesson-area bg-color-white">{error}</div>;
    }

    if (!courseData) {
        return <div className="rbt-lesson-area bg-color-white">No course data available</div>;
    }

    return (
        <div className="rbt-lesson-area bg-color-white">
            <div className="rbt-lesson-content-wrapper">
                <div className="rbt-lesson-leftsidebar">
                    <div className="rbt-course-feature-inner rbt-search-activation">
                        <div className="section-title">
                            <h4 className="rbt-title-style-3">Course Content</h4>
                        </div>
                        <div className="lesson-search-wrapper">
                            <form action="#" className="rbt-search-style-1">
                                <input
                                    className="rbt-search-active"
                                    type="text"
                                    placeholder="Search Lesson"
                                />
                                <button className="search-btn disabled">
                                    <i className="feather-search" />
                                </button>
                            </form>
                        </div>
                        <hr className="mt--10" />
                        <div className="rbt-accordion-style rbt-accordion-02 for-right-content accordion">
                            <div className="accordion" id="accordionExampleb2">
                                {courseData.categories.map((category, index) => {
                                    const categoryProgress = progressData?.category_progress.find(
                                        cp => cp.category_id === category.id
                                    );
                                    return (
                                        <div key={category.id} className="accordion-item card">
                                            <h2 className="accordion-header card-header" id={`heading${category.id}`}>
                                                <button
                                                    className={`accordion-button ${accordionStates[`section${index + 1}`] ? 'collapsed' : ''}`}
                                                    type="button"
                                                    onClick={() => toggleAccordion(`section${index + 1}`)}
                                                    aria-expanded={!accordionStates[`section${index + 1}`]}
                                                    aria-controls={`collapse${category.id}`}
                                                >
                                                    <div className="d-flex align-items-center justify-content-between w-100">
                                                        <span className="category-name text-truncate me-2">{category.name}</span>
                                                        <span className="rbt-badge-5">
                                                            {categoryProgress?.completed_videos || 0}/{category.videos.length}
                                                        </span>
                                                    </div>
                                                </button>
                                            </h2>
                                            <div
                                                id={`collapse${category.id}`}
                                                className={`accordion-collapse collapse ${!accordionStates[`section${index + 1}`] ? 'show' : ''}`}
                                                aria-labelledby={`heading${category.id}`}
                                            >
                                                <div className="accordion-body card-body">
                                                    <ul className="rbt-course-main-content liststyle">
                                                        {category.videos.map((video) => {
                                                            const videoProgressData = videoProgress[video.id] || {};
                                                            return (
                                                                <li key={video.id}>
                                                                    <a 
                                                                        href="#" 
                                                                        onClick={(e) => {
                                                                            e.preventDefault();
                                                                            handleVideoClick(video);
                                                                        }}
                                                                        className={currentVideo?.id === video.id ? 'active' : ''}
                                                                    >
                                                                        <div className="course-content-left">
                                                                            <i className="feather-play-circle" />{" "}
                                                                            <span className="text">{video.title}</span>
                                                                        </div>
                                                                        <div className="course-content-right">
                                                                            {video.video_duration && (
                                                                                <span className="min-lable">
                                                                                    {Math.floor(video.video_duration / 60)} min
                                                                                </span>
                                                                            )}
                                                                            <span className={`rbt-check ${videoProgressData.is_completed ? '' : 'unread'}`}>
                                                                                <i className={videoProgressData.is_completed ? "feather-check" : "feather-circle"} />
                                                                            </span>
                                                                        </div>
                                                                    </a>
                                                                </li>
                                                            );
                                                        })}
                                                    </ul>
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    </div>
                </div>
                <div className="rbt-lesson-rightsidebar overflow-hidden lesson-video">
                    <div className="lesson-top-bar">
                        <div className="lesson-top-left">
                            <div className="rbt-lesson-toggle">
                                <button
                                    className="lesson-toggle-active btn-round-white-opacity"
                                    title="Back to Dashboard"
                                    onClick={() => navigate('/StudentDashboard')}
                                >
                                    <i className="feather-arrow-left" />
                                </button>
                            </div>
                            <h5>{courseData.title}</h5>
                        </div>
                        <div className="lesson-top-right">
                            <div className="rbt-btn-close">
                                <a
                                    href="#"
                                    onClick={(e) => {
                                        e.preventDefault();
                                        navigate('/StudentDashboard');
                                    }}
                                    title="Go Back to Dashboard" 
                                    className="rbt-round-btn"
                                >
                                    <i className="feather-x" />
                                </a>
                            </div>
                        </div>
                    </div>
                    <div className="inner">
                        <div className="plyr__video-embed rbtplayer" style={{ position: 'relative', width: '100%' }}>
                            <div style={{ position: 'relative', width: '100%', paddingBottom: '56.25%' }}>
                                <div
                                    ref={watermarkRef}
                                    className="watermark"
                                    style={{
                                        position: 'absolute',
                                        left: watermarkPos.x,
                                        top: watermarkPos.y,
                                        fontSize: 'clamp(16px, 4vw, 36px)',
                                        color: 'rgba(255, 255, 255, 0.7)',
                                        fontWeight: 'bold',
                                        pointerEvents: 'none',
                                        userSelect: 'none',
                                        whiteSpace: 'nowrap',
                                        zIndex: 1,
                                    }}
                                >
                                    Day Trading
                                </div>
                                <div
                                    id="vimeo-player"
                                    style={{
                                        position: 'absolute',
                                        top: 0,
                                        left: 0,
                                        width: '100%',
                                        height: '100%',
                                    }}
                                />
                            </div>

                            {/* Custom Controls - Moved inside the player container */} 
                            <div className="rbt-player-controls">
                                <div className="rbt-progress-bar-wrapper" onClick={handleSeek}>
                                    <div className="rbt-progress-bar" style={{ width: `${(currentTime / duration) * 100 || 0}%` }}></div>
                                </div>
                                <div className="rbt-controls-row">
                                    <div className="rbt-controls-left">
                                        <button onClick={togglePlayPause} className="rbt-control-button">
                                            <i className={`feather-${isPlaying ? 'pause' : 'play'}`} />
                                        </button>
                                        <span className="rbt-time-display">{formatTime(currentTime)} / {formatTime(duration)}</span>
                                    </div>
                                    <div className="rbt-controls-right">
                                        <button onClick={toggleMute} className="rbt-control-button">
                                            <i className={`feather-${isMuted ? 'volume-x' : 'volume-2'}`} />
                                        </button>
                                        <input
                                            type="range"
                                            min="0"
                                            max="1"
                                            step="0.01"
                                            value={volume}
                                            onChange={handleVolumeChange}
                                            className="rbt-volume-slider"
                                        />
                                    </div>
                                </div>
                            </div>

                        </div>

                        <div className="content">
                            <div className="section-title">
                                <h4>About Lesson</h4>
                                <p>{courseData.description}</p>
                            </div>
                        </div>
                    </div>
                    <div className="bg-color-extra2 ptb--15 overflow-hidden">
                        <div className="rbt-button-group">
                            <button
                                className="rbt-btn icon-hover icon-hover-left btn-md bg-primary-opacity"
                                onClick={handlePreviousVideo}
                                disabled={currentCategoryIndex === 0 && currentVideoIndex === 0}
                            >
                                <span className="btn-icon">
                                    <i className="feather-arrow-left" />
                                </span>
                                <span className="btn-text">Previous</span>
                            </button>
                            <button 
                                className="rbt-btn icon-hover btn-md"
                                onClick={handleNextVideo}
                                disabled={
                                    currentCategoryIndex === courseData?.categories.length - 1 && 
                                    currentVideoIndex === courseData?.categories[currentCategoryIndex]?.videos.length - 1
                                }
                            >
                                <span className="btn-text">Next</span>
                                <span className="btn-icon">
                                    <i className="feather-arrow-right" />
                                </span>
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Lession;
